import React, { useState, useCallback } from 'react';
import RealEstatesItems from './RealEstatesItems';
import RealEstatesCreateModal from './modals/RealEstatesCreateModal';
import RealEstatesDeleteModal from './modals/RealEstatesDeleteModal';
import RealEstatesEditModal from './modals/RealEstatesEditModal';

export default function RealEstatesList({
  realEstates,
  realEstateTypes,
  handleCreate,
  handleEdit,
  handleDelete,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRealEstate, setSelectedRealEstate] = useState(null);
  const itemsPerPage = 9;
  const totalPages = Math.ceil(realEstates.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top smoothly
  };

  const paginatedItems = realEstates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleCreateModal = useCallback(() => {
    setIsCreateModalOpen((prev) => !prev);
  }, []);

  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((prev) => !prev);
  }, []);

  const toggleEditModal = useCallback(() => {
    setIsEditModalOpen((prev) => !prev);
  }, []);

  const handleDeleteClick = (realEstate) => {
    setSelectedRealEstate(realEstate);
    toggleDeleteModal();
  };

  const handleEditClick = (realEstate) => {
    setSelectedRealEstate(realEstate);
    toggleEditModal();
  };

  return (
    <>
      <div className="mt-32 px-14">
        <section className="container px-4 mx-auto pt-6 pb-12">
          <div className="bg-gray-100 border border-gray-300 p-6 rounded-lg shadow-2xl">
            <div className="flex items-center gap-x-3">
              <h2 className="text-4xl tracking-wide font-medium leading-none text-gray-900">
                Inmuebles
              </h2>
              <button
                onClick={toggleCreateModal}
                className="ml-auto flex items-center tracking-wide font-medium text-gray-500 transition-colors duration-500 rounded-lg group hover:text-customBlue"
              >
                <svg
                  className="mr-1 mt-1 h-5 w-5 font-medium text-gray-500 transition-colors duration-500 group-hover:text-customBlue"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {' '}
                  <path stroke="none" d="M0 0h24v24H0z" />{' '}
                  <circle cx="12" cy="12" r="9" />{' '}
                  <line x1="9" y1="12" x2="15" y2="12" />{' '}
                  <line x1="12" y1="9" x2="12" y2="15" />
                </svg>
                Agregar inmueble
              </button>
            </div>
            <div className="flex flex-col mt-6 px-6">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle custom-scrollbar">
                  <div className="overflow-hidden border border-gray-200 md:rounded-lg custom-scrollbar">
                    <table className="min-w-full divide-y divide-gray-200 table-fixed">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Nombre
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Descripción
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Precio
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Ciudad
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Sector
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Área
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Piso
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Habitaciones
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Baños
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Cocinas
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Balcón
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Patios
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Garaje
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Estado
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Etiquetas
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3.5 text-sm font-medium text-center rtl:text-right text-gray-800"
                          >
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      {paginatedItems.map((realEstate, index) => (
                        <RealEstatesItems
                          key={index}
                          index={index}
                          realEstate={realEstate}
                          realEstateTypes={realEstateTypes}
                          handleDeleteClick={handleDeleteClick}
                          handleEditClick={handleEditClick}
                        />
                      ))}
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <nav
              aria-label="Page navigation example"
              className="mt-4 flex justify-center"
            >
              <ul className="flex items-center -space-x-px h-8 text-sm">
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center transition-colors duration-500 px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <svg
                      className="w-2.5 h-2.5 rtl:rotate-180"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 6 10"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 1 1 5l4 4"
                      />
                    </svg>
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handlePageChange(index + 1)}
                      className={`flex items-center justify-center transition-colors duration-500 px-3 h-8 leading-tight ${
                        currentPage === index + 1
                          ? 'border border-customBlue text-customBlue bg-blue-50 hover:bg-blue-100 hover:text-customBlue font-bold'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-200 hover:text-gray-900'
                      }`}
                      aria-current={
                        currentPage === index + 1 ? 'page' : undefined
                      }
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center transition-colors duration-500 px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <svg
                      className="w-2.5 h-2.5 rtl:rotate-180"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 6 10"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m1 9 4-4-4-4"
                      />
                    </svg>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </section>
      </div>
      {isCreateModalOpen && (
        <RealEstatesCreateModal
          handleCreate={handleCreate}
          toggleModal={toggleCreateModal}
          realEstateTypes={realEstateTypes}
        />
      )}
      {isEditModalOpen && (
        <RealEstatesEditModal
          realEstate={selectedRealEstate}
          realEstateTypes={realEstateTypes}
          handleEdit={handleEdit}
          toggleModal={toggleEditModal}
        />
      )}
      {isDeleteModalOpen && (
        <RealEstatesDeleteModal
          realEstate={selectedRealEstate}
          handleDelete={handleDelete}
          toggleModal={toggleDeleteModal}
        />
      )}
    </>
  );
}
